/**
 * GIF / sticker proxy policy — compile-time-fixed CDN proxy base URL.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/GifProxyPolicy.swift`.
 *
 * ALL GIF and sticker media URLs sent to the client MUST pass through the
 * Kaether CDN reverse-proxy before being embedded in messages or stored in the
 * sticker manifest. This prevents the provider (Tenor, GIPHY, …) from seeing
 * the user's IP address, and prevents any party from embedding arbitrary
 * third-party URLs in a message payload.
 *
 * ## Design invariants
 *
 * 1. `PROXY_BASE_URL` is a **compile-time constant** — it is never read from a
 *    server-supplied config or environment variable.
 * 2. Only HTTPS is accepted.
 * 3. The literal substring `://` is rejected anywhere after the proxy base in
 *    the queryPath. An attacker might encode `?redirect=http://evil.com` in the
 *    path to fool a naïve validator; by rejecting `://` outright we block that
 *    class of open-redirect attacks regardless of URL-encoding tricks applied at
 *    the outer layer.
 * 4. `validateResponseURL` validates every URL in a redirect chain, not just
 *    the final destination, so a mid-chain redirect to an external host is also
 *    rejected.
 *
 * Pure logic. No `fetch()` calls. No SolidJS imports.
 */

// ---------------------------------------------------------------------------
// Compile-time constant
// ---------------------------------------------------------------------------

/**
 * Base URL of the Kaether CDN GIF/sticker proxy.
 *
 * This value is baked in at build time. It MUST NOT be replaced by a runtime
 * configuration fetch.
 */
export const PROXY_BASE_URL = "https://cdn.kaether.fr/gifproxy/v1/" as const;

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` if `url` is a safe, on-proxy GIF or sticker URL.
 *
 * Acceptance criteria (all must hold):
 *  - The URL starts with HTTPS (i.e. the scheme is `https://`).
 *  - The URL starts with `PROXY_BASE_URL` (exact prefix match, case-sensitive).
 *  - The portion of the URL **after** the proxy base (the "query path") does
 *    not contain the literal substring `://`. This closes the open-redirect
 *    attack vector where a provider-supplied redirect parameter like
 *    `?next=http://evil.com` would escape the proxy.
 *
 * @param url - Absolute URL string to validate.
 */
export function validateProxyURL(url: string): boolean {
  // 1. Must be HTTPS — http:// or any other scheme is rejected immediately.
  if (!url.startsWith("https://")) {
    return false;
  }

  // 2. Must start with the compile-time proxy base.
  if (!url.startsWith(PROXY_BASE_URL)) {
    return false;
  }

  // 3. The queryPath is everything after the proxy base.
  //    Reject if it contains the literal `://`, which signals an attempted
  //    external-redirect injection (e.g. ?redirect=https://evil.com/...).
  const queryPath = url.slice(PROXY_BASE_URL.length);
  if (queryPath.includes("://")) {
    return false;
  }

  return true;
}

/**
 * Validates an HTTP redirect chain returned by the proxy.
 *
 * Every URL in `redirectChain` must satisfy `validateProxyURL`. If any step
 * leaves the proxy domain — for example, if the CDN mis-configured a redirect
 * to the upstream provider — the chain is rejected in its entirety.
 *
 * @param redirectChain - Ordered list of URLs from the initial request to the
 *   final response. May be empty (a single non-redirecting request); in that
 *   case `true` is returned.
 */
export function validateResponseURL(redirectChain: readonly string[]): boolean {
  return redirectChain.every((url) => validateProxyURL(url));
}
